<template>
  <div class="teacher-availability-display" :class="{ compact: compact, 'modal-view': modalView }">
    <div class="header-section">
      <h4>Müsaitlik Bilgileri</h4>
      
      <!-- Hafta Bilgisi (Sadece Gösterim) -->
      <div class="current-week-info">
        <span class="week-label">Hafta:</span>
        <span class="week-date">{{ formatWeekDate(currentWeekStart) }}</span>
        <span class="week-number">({{ currentWeekNumber }}. Hafta)</span>
      </div>
    </div>
    
    <div v-if="loading" class="loading">
      Müsaitlik bilgileri yükleniyor...
    </div>
    
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    
    <div v-else-if="viewMode === 'edit' || (availability && Object.keys(availability).length > 0)" class="availability-content">
      <!-- Hafta İçi Günleri -->
      <div v-if="filteredWeekdays.length > 0" class="weekday-section">
        <h5>Hafta İçi</h5>
        <div class="weekday-scroll-container">
          <div class="weekday-content">
            <div v-for="day in filteredWeekdays" :key="day.key" class="day-column">
              <div class="day-header">{{ day.name }}</div>
              <div class="time-slots-column">
                <div
                  v-for="slot in weekdaySlots"
                  :key="slot.id"
                  :class="[
                    'time-slot',
                    slot.disabled ? 'disabled' : getSlotClass(day.key, slot.id)
                  ]"
                  @click="!slot.disabled && (viewMode === 'edit' ? toggleSlotAvailability(day.key, slot.id) : handleSlotClick(day.key, slot.id, slot.time))"
                  :style="{ cursor: !slot.disabled && (viewMode === 'edit' || isSlotClickable(day.key, slot.id)) ? 'pointer' : 'default' }"
                >
                  <span class="time-text">{{ slot.time }}</span>
                  <span v-if="slot.disabled" class="break-text">Öğle Arası</span>
                  <span v-else class="slot-status">
                    {{ getSlotStatus(day.key, slot.id) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Hafta Sonu Günleri -->
      <div v-if="filteredWeekends.length > 0" class="weekend-section">
        <h5>Hafta Sonu</h5>
        <div class="weekend-scroll-container">
          <div class="weekend-content">
            <div v-for="day in filteredWeekends" :key="day.key" class="day-column">
              <div class="day-header">{{ day.name }}</div>
              <div class="time-slots-column">
                                 <div
                   v-for="slot in weekendSlots"
                     :key="slot.id"
                     :class="[
                       'time-slot',
                       slot.disabled ? 'disabled' : getSlotClass(day.key, slot.id)
                     ]"
                     @click="!slot.disabled && (viewMode === 'edit' ? toggleSlotAvailability(day.key, slot.id) : handleSlotClick(day.key, slot.id, slot.time))"
                     :style="{ cursor: !slot.disabled && (viewMode === 'edit' || isSlotClickable(day.key, slot.id)) ? 'pointer' : 'default' }"
                   >
                     <span class="time-text">{{ slot.time }}</span>
                     <span v-if="slot.disabled" class="break-text">Öğle Arası</span>
                     <span v-else class="slot-status">
                       {{ getSlotStatus(day.key, slot.id) }}
                     </span>
                   </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
    
    <div v-else class="no-availability">
      Bu öğretmen için henüz müsaitlik bilgisi girilmemiş.
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { generateWeekdaySlots, generateWeekendSlots } from '@/utils/slotGenerator'

export default {
  name: 'TeacherAvailabilityDisplay',
  props: {
    teacherId: {
      type: [Number, String],
      required: true
    },
    compact: {
      type: Boolean,
      default: false
    },
    modalView: {
      type: Boolean,
      default: false
    },
    viewMode: {
      type: String,
      default: 'view', // 'view' (müsaitlik incele), 'select' (randevu seç) veya 'edit' (düzenle)
      validator: (value) => ['view', 'select', 'edit'].includes(value)
    },
    onSlotClick: {
      type: Function,
      default: null // Slot'a tıklayınca çağrılacak fonksiyon
    },
    refresh: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore()
    const availability = ref({})
    const loading = ref(false)
    const error = ref('')
    const currentWeekStart = ref(new Date())
    const currentWeekNumber = ref(0)

    const weekdays = [
      { key: 'monday', name: 'Pazartesi' },
      { key: 'tuesday', name: 'Salı' },
      { key: 'wednesday', name: 'Çarşamba' },
      { key: 'thursday', name: 'Perşembe' }
    ]

    const weekends = [
      { key: 'saturday', name: 'Cumartesi' },
      { key: 'sunday', name: 'Pazar' }
    ]

    // Slot ayarları store'dan gelir (system_config), boşsa fetch tetiklenir onMounted'da
    const slotSettings = computed(() => store.getters.getSlotSettings)
    const weekdaySlots = computed(() => generateWeekdaySlots(slotSettings.value))
    const weekendSlots = computed(() => generateWeekendSlots(slotSettings.value))

    // "HH:MM" → dakika (parse başarısız veya boşsa fallback 08:00 = 480)
    const hhmmToMin = (hhmm) => {
      if (!hhmm || typeof hhmm !== 'string') return 480
      const [h, m] = hhmm.split(':').map(Number)
      if (Number.isNaN(h) || Number.isNaN(m)) return 480
      return h * 60 + m
    }
    // Bir gün için day_start'ı dakika cinsinden döner (hafta içi/sonu ayrı)
    const dayStartMin = (dayNumber) => {
      const isWeekend = dayNumber === 0 || dayNumber === 6
      const key = isWeekend ? 'day_start_weekend' : 'day_start_weekday'
      return hhmmToMin(slotSettings.value?.[key])
    }

    // Hafta başlangıç tarihini hesapla
    const getWeekStartDate = (date) => {
      // Turkey timezone (+3) için tarihi ayarla
      const turkeyDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Istanbul"}))
      
      // Pazar 19:00 kuralı: Pazar 19:00'dan sonra gelecek haftayı göster
      const isSundayAfter7PM = turkeyDate.getDay() === 0 && turkeyDate.getHours() >= 19
      
      if (isSundayAfter7PM) {
        // Pazar 19:00'dan sonra: Gelecek haftayı göster
        turkeyDate.setDate(turkeyDate.getDate() + 1) // Pazartesi'ye geç
      }
      
      const weekday = turkeyDate.getDay()
      const daysToMonday = weekday === 0 ? 6 : weekday - 1
      const monday = new Date(turkeyDate)
      monday.setDate(turkeyDate.getDate() - daysToMonday)
      
      return monday
    }

    // Hafta numarasını hesapla
    const getWeekNumber = (date) => {
      // Turkey timezone (+3) için tarihi ayarla
      const turkeyDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Istanbul"}))
      
      // Yılın ilk günü (1 Ocak) için Turkey timezone kullan
      const firstDayOfYear = new Date(turkeyDate.getFullYear(), 0, 1)
      const turkeyFirstDay = new Date(firstDayOfYear.toLocaleString("en-US", {timeZone: "Europe/Istanbul"}))
      
      // ISO 8601 standardına göre hafta hesaplama
      // Pazartesi haftanın başlangıcı, Pazar haftanın son günü
      const firstDayWeekday = turkeyFirstDay.getDay()
      const daysToMonday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1
      
      // Yılın ilk Pazartesi'sini bul (Turkey timezone'da)
      const firstMonday = new Date(turkeyFirstDay)
      firstMonday.setDate(turkeyFirstDay.getDate() - daysToMonday)
      
      // Verilen tarihten ilk Pazartesi'ye kadar geçen hafta sayısını hesapla
      const weeksDiff = Math.floor((turkeyDate.getTime() - firstMonday.getTime()) / (7 * 24 * 60 * 60 * 1000))
      
      // ISO 8601 standardına göre: Eğer ilk Pazartesi önceki yıldaysa, hafta 1'den başlar
      let weekNumber = weeksDiff + 1
      
      // Eğer ilk Pazartesi önceki yıldaysa (30 Aralık gibi), hafta sayısını 1 azalt
      if (firstMonday.getFullYear() < turkeyDate.getFullYear()) {
        weekNumber = weekNumber - 1

      }
      

      
      return weekNumber
    }

    // İlk yüklemede bu haftayı ayarla (Backend mantığıyla uyumlu)
    onMounted(async () => {
      // Slot ayarlarını yükle (henüz store'da yoksa)
      if (!slotSettings.value) {
        await store.dispatch('fetchSlotSettings')
      }

      const now = new Date()
      const currentDay = now.getDay() // 0 = Pazar, 1 = Pazartesi, ...
      const currentHour = now.getHours()
      const currentMinute = now.getMinutes()
      
      // Pazar 19:00'dan sonra VEYA Pazartesi'den itibaren gelecek haftayı göster
      const isSundayAfter7PM = currentDay === 0 && currentHour >= 19 && currentMinute >= 0
      const isAfterReset = isSundayAfter7PM || currentDay !== 0
      
      if (isAfterReset) {
        // Reset sonrası: Gelecek haftayı hesapla
        const nextWeek = new Date(now)
        nextWeek.setDate(now.getDate() + 7)
        currentWeekStart.value = getWeekStartDate(nextWeek)
        currentWeekNumber.value = getWeekNumber(nextWeek)

      } else {
        // Reset öncesi: Normal hafta
        currentWeekStart.value = getWeekStartDate(now)
        currentWeekNumber.value = getWeekNumber(now)

      }
      
      fetchAvailability()
    })

    const fetchAvailability = async () => {
      if (!props.teacherId) return
      

      
      loading.value = true
      error.value = ''
      
      try {
        // Hafta başlangıç tarihini YYYY-MM-DD formatında gönder (yerel timezone ile)
        const weekStartStr = currentWeekStart.value.toLocaleDateString('sv-SE')
        const result = await store.dispatch('fetchTeacherAvailability', {
          teacherId: props.teacherId,
          weekStart: weekStartStr
        })
        
        
        
        if (result.success) {
          availability.value = result.data.availability || {}
          currentWeekStart.value = new Date(result.data.week_start_date)
          currentWeekNumber.value = result.data.week_number
          
          
        } else {
          error.value = result.error
          console.error('❌ Müsaitlik yüklenemedi:', result.error)
        }
      } catch (err) {
        error.value = 'Müsaitlik bilgileri alınamadı'
        console.error('❌ Müsaitlik getirme hatası:', err)
      } finally {
        loading.value = false
      }
    }



    const formatWeekDate = (date) => {
      const start = new Date(date)
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      
      const startStr = start.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
      const endStr = end.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      
      return `${startStr} - ${endStr}`
    }

    const getSlotClass = (dayKey, slotId) => {
      // Öğle arası kontrolü - Hafta içi sadece 12:00-12:50, hafta sonu 12:50-13:30
      if (slotId.includes('12:00-12:50')) {
        // Hafta içi günlerde öğle arası
        if (['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(dayKey)) {
          return 'disabled'
        }
      }
      
      if (slotId.includes('12:50-13:30')) {
        // Hafta sonu günlerde öğle arası
        if (['saturday', 'sunday'].includes(dayKey)) {
          return 'disabled'
        }
      }
      
      // Normal müsaitlik kontrolü
      const daySlots = availability.value[dayKey]
      if (!daySlots) return 'not-available'
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      if (!slot) return 'not-available'
      
      if (slot.is_booked) {
        // Öğrenci tarafından alınan slot'lar farklı görünsün
        if (slot.booked_by && slot.booked_by !== 'admin') {
          return 'booked-by-student'
        }
        return 'booked'
      }
      
      // viewMode'a göre farklı davran
      if (props.viewMode === 'select') {
        // Randevu seçimi modu: Geçmiş günler için disabled
        const today = new Date()
        const currentDay = today.getDay() // 0 = Pazar, 1 = Pazartesi, ...
        
        // Pazar günü 19:00'dan sonra gelecek hafta gösterildiği için, "bugün" kontrolü yapılmaz
        if (currentDay === 0 && today.getHours() >= 19) {

          return 'available' // Gelecek haftanın tüm günleri müsait
        }
        
        // Gün adını (Pazartesi=1 ... Pazar=7) olacak şekilde sayıya çevir
        const dayToMonNumber = {
          'monday': 1, 'tuesday': 2, 'wednesday': 3,
          'thursday': 4, 'friday': 5, 'saturday': 6, 'sunday': 7
        }
        const slotDayIndex = dayToMonNumber[dayKey]
        const currentDayIndex = currentDay === 0 ? 7 : currentDay
        if (slotDayIndex !== undefined) {
          // Geçmiş günler (aynı hafta içinde Pazartesi bazlı kıyaslama)
          if (slotDayIndex < currentDayIndex) {
            return 'disabled'
          }
        }
      }
      
      return 'available'
    }

    const getSlotStatus = (dayKey, slotId) => {
      // Öğle arası kontrolü - Hafta içi sadece 12:00-12:50, hafta sonu 12:50-13:30
      if (slotId.includes('12:00-12:50')) {
        // Hafta içi günlerde öğle arası
        if (['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(dayKey)) {
          return 'Öğle Arası'
        }
      }
      
      if (slotId.includes('12:50-13:30')) {
        // Hafta sonu günlerde öğle arası
        if (['saturday', 'sunday'].includes(dayKey)) {
          return 'Öğle Arası'
        }
      }
      
      // Normal müsaitlik kontrolü
      const daySlots = availability.value[dayKey]
      if (!daySlots) return '✗ Müsait Değil'
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      if (!slot) return '✗ Müsait Değil'
      
      if (slot.is_booked) {
        // Öğrenci tarafından alınan slot'lar farklı görünsün
        if (slot.booked_by && slot.booked_by !== 'admin') {
          return '✗ Öğrenci Aldı'
        }
        return '✗ Alındı'
      }
      
      // viewMode'a göre farklı davran
      if (props.viewMode === 'select') {
        // Randevu seçimi modu: Geçmiş günler için disabled
        const today = new Date()
        const currentDay = today.getDay() // 0 = Pazar, 1 = Pazartesi, ...
        
        // Pazar günü 19:00'dan sonra gelecek hafta gösterildiği için, "bugün" kontrolü yapılmaz
        if (currentDay === 0 && today.getHours() >= 19) {
          return '✓ Müsait' // Gelecek haftanın tüm günleri müsait
        }
        
        // Gün adını (Pazartesi=1 ... Pazar=7) olacak şekilde sayıya çevir
        const dayToMonNumber = {
          'monday': 1, 'tuesday': 2, 'wednesday': 3,
          'thursday': 4, 'friday': 5, 'saturday': 6, 'sunday': 7
        }
        const slotDayIndex = dayToMonNumber[dayKey]
        const currentDayIndex = currentDay === 0 ? 7 : currentDay
        if (slotDayIndex !== undefined) {
          // Geçmiş günler (aynı hafta içinde Pazartesi bazlı kıyaslama)
          if (slotDayIndex < currentDayIndex) {
            return '✗ Geçmiş Gün'
          }
        }
      }
      
      return '✓ Müsait'
    }

    const getDayName = (dayKey) => {
      const allDays = [...weekdays, ...weekends]
      const day = allDays.find(d => d.key === dayKey)
      return day ? day.name : dayKey
    }



    // viewMode="select" için günleri filtrele
    const filteredWeekdays = computed(() => {
      if (props.viewMode !== 'select') return weekdays
      
      const today = new Date()
      const currentDay = today.getDay() // 0 = Pazar, 1 = Pazartesi, ...
      const currentHour = today.getHours()
      
      // Pazar günü 19:00'dan önce hiçbir gün gösterilmez (haftanın son günü)
      if (currentDay === 0 && currentHour < 19) {
        return []
      }
      
      // Pazar günü 19:00'dan sonra gelecek haftanın TÜM günleri gösterilir
      if (currentDay === 0 && currentHour >= 19) {
        return weekdays // Gelecek haftanın tüm hafta içi günleri
      }
      
      // Diğer günler için normal filtreleme
      // Gün adını sayıya çevir
      const dayToNumber = {
        'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4
      }
      
      const nowMin = today.getHours() * 60 + today.getMinutes()

      const filtered = weekdays.filter(day => {
        const dayNumber = dayToNumber[day.key]

        // Bugün için - day_start saati geçtiyse gösterme
        if (dayNumber === currentDay) {
          return nowMin < dayStartMin(dayNumber)
        }

        // Gelecek günler için - sadece bugünden sonraki günler
        if (dayNumber > currentDay) {
          return true
        }
        
        // Geçmiş günler için - listelenmez
        return false
      })
      

      return filtered
    })

    const filteredWeekends = computed(() => {
      if (props.viewMode !== 'select') return weekends
      
      const today = new Date()
      const currentDay = today.getDay() // 0 = Pazar, 1 = Pazartesi, ...
      const currentHour = today.getHours()
      
      // Pazar günü 19:00'dan önce hiçbir gün gösterilmez (haftanın son günü)
      if (currentDay === 0 && currentHour < 19) {
        return []
      }
      
      // Pazar günü 19:00'dan sonra gelecek haftanın TÜM günleri gösterilir
      if (currentDay === 0 && currentHour >= 19) {
        return weekends // Gelecek haftanın tüm hafta sonu günleri
      }
      
      // Diğer günler için normal filtreleme
      // Gün adını sayıya çevir
      const dayToNumber = {
        'saturday': 6, 'sunday': 0
      }
      
      const nowMin = today.getHours() * 60 + today.getMinutes()

      const filtered = weekends.filter(day => {
        const dayNumber = dayToNumber[day.key]

        // Bugün için - day_start saati geçtiyse gösterme
        if (dayNumber === currentDay) {
          return nowMin < dayStartMin(dayNumber)
        }

        // Hafta sonu günleri için özel mantık
        if (day.key === 'saturday') {
                  // Cumartesi her zaman göster (hafta içi günlerden sonra)
        return true
        } else if (day.key === 'sunday') {
          // Pazar günü için özel kontrol
          if (currentDay === 0) {
            // Bugün Pazar ise, day_start kontrolü
            return nowMin < dayStartMin(0)
          } else {
            // Bugün Pazar değilse, Pazar gelecek gün olarak göster
            return true
          }
        }
        
        // Diğer durumlar için
        return true
      })
      

      return filtered
    })

    // filteredWeekdaySlots kaldırıldı - gerekli değil
    // Gün filtreleme (filteredWeekdays) ve saat filtreleme (getSlotClass/getSlotStatus) yeterli

    // filteredWeekendSlots kaldırıldı - gerekli değil
    // Gün filtreleme (filteredWeekends) ve saat filtreleme (getSlotClass/getSlotStatus) yeterli

    // Slot'a tıklama fonksiyonu
    const handleSlotClick = (dayKey, slotId, slotTime) => {
      if (props.onSlotClick && props.viewMode === 'select') {
        // Sadece müsait saatlere tıklanabilir
        const daySlots = availability.value[dayKey]
        if (daySlots) {
          const slot = daySlots.find(s => s.time_slot === slotId)
          if (slot && !slot.is_booked) {
            props.onSlotClick(dayKey, slotId, slotTime)
          }
        }
      }
    }

    // Edit modunda slot müsaitlik durumunu değiştirme
    const toggleSlotAvailability = async (dayKey, slotId) => {
      if (props.viewMode !== 'edit') return
      
      try {
        // Mevcut slot'ı bul
        const daySlots = availability.value[dayKey]
        if (!daySlots) {
          // Eğer gün yoksa, yeni gün oluştur
          if (!availability.value[dayKey]) {
            availability.value[dayKey] = []
          }
        }
        
        const existingSlot = availability.value[dayKey]?.find(s => s.time_slot === slotId)
        
        if (existingSlot) {
          // Öğrenci tarafından alınan slot'lara dokunulamasın
          if (existingSlot.booked_by && existingSlot.booked_by !== 'admin') {
    
            return
          }
          
          // Slot'ın mevcut durumunu kontrol et
          if (existingSlot.is_booked) {
            // Alınan slot'ı müsait yap
            existingSlot.is_booked = false
            existingSlot.booked_by = null

          } else {
            // Müsait slot'ı müsait değil yap (slot'ı tamamen kaldır)
            const slotIndex = availability.value[dayKey].findIndex(s => s.time_slot === slotId)
            if (slotIndex > -1) {
              availability.value[dayKey].splice(slotIndex, 1)

            }
          }
        } else {
          // Yeni slot ekle (müsait olarak)
          const newSlot = {
            time_slot: slotId,
            is_booked: false,
            booked_by: null
          }
          availability.value[dayKey].push(newSlot)

        }
        
        // Veritabanını güncelle
        await updateAvailabilityInDatabase(dayKey, slotId)
        
      } catch (error) {
        console.error('Slot müsaitlik durumu güncellenirken hata:', error)
      }
    }

    // Veritabanında müsaitlik durumunu güncelle
    const updateAvailabilityInDatabase = async (dayKey, slotId) => {
      try {
        // Hafta başlangıç tarihini YYYY-MM-DD formatında gönder (yerel timezone ile)
        const weekStartStr = currentWeekStart.value.toLocaleDateString('sv-SE')
        
        // Burada backend API'ye güncelleme isteği gönderilecek
        
        
        
      } catch (error) {
        console.error('Veritabanı güncelleme hatası:', error)
      }
    }

    // Slot'ın tıklanabilir olup olmadığını kontrol et
    const isSlotClickable = (dayKey, slotId) => {
      if (props.viewMode !== 'select') return false
      
      const daySlots = availability.value[dayKey]
      if (!daySlots) return false
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      return slot && !slot.is_booked
    }

    // Önceki hafta butonunun disable olup olmadığını kontrol et
    const isPreviousWeekDisabled = computed(() => {
      const now = new Date()
      const currentDay = now.getDay() // 0 = Pazar, 1 = Pazartesi, ...
      const currentHour = now.getHours()
      
      // Pazar 19:00'dan sonra geçmiş haftalara gidilemez
      if (currentDay === 0 && currentHour >= 19) {
        return true
      }
      
      // Mevcut hafta başlangıcı ile şu anki hafta başlangıcını karşılaştır
      const currentWeekStart = getWeekStartDate(now)
      const canGoToPreviousWeek = currentWeekStart.value > currentWeekStart
      
      return !canGoToPreviousWeek
    })

    watch(() => props.teacherId, () => {
      fetchAvailability()
    })

    // refresh prop'u değiştiğinde verileri yeniden yüklen
    watch(() => props.refresh, (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {

        
        // Hemen yeniden yükle
        fetchAvailability()
        
        // Ek olarak 200ms sonra tekrar yükle (güvenlik için)
        setTimeout(() => {

          fetchAvailability()
        }, 200)
      }
    }, { immediate: false })

    return {
      availability,
      loading,
      error,
      filteredWeekdays,
      filteredWeekends,
      weekdays: filteredWeekdays,
      weekends: filteredWeekends,
      weekdaySlots: weekdaySlots,
      weekendSlots: weekendSlots,
      currentWeekStart,
      currentWeekNumber,
      formatWeekDate,
      getSlotClass,
      getSlotStatus,
      getDayName,
      handleSlotClick,
      isSlotClickable,
      toggleSlotAvailability
    }
  }
}
</script>

<style scoped>
.teacher-availability-display {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

/* TeacherAvailabilityDisplay responsive tasarım */
@media (max-width: 1024px) {
  .teacher-availability-display {
    padding: 1.25rem;
  }
}

@media (max-width: 768px) {
  .teacher-availability-display {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .header-section h4 {
    font-size: 1.1rem;
  }
  
  .current-week-info {
    flex-direction: column;
    gap: 0.25rem;
    text-align: center;
    padding: 0.75rem;
  }
  
  .weekday-scroll-container,
  .weekend-scroll-container {
    overflow-x: auto;
  }
  
  .weekday-content,
  .weekend-content {
    min-width: 600px; /* Minimum genişlik */
  }
}

@media (max-width: 480px) {
  .teacher-availability-display {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
  
  .header-section h4 {
    font-size: 1rem;
  }
  
  .current-week-info {
    padding: 0.5rem;
  }
  
  .weekday-content,
  .weekend-content {
    min-width: 500px; /* Daha küçük ekranlar için */
  }
}

.header-section {
  margin-bottom: 1.5rem;
}

.teacher-availability-display h4 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.2rem;
  border-bottom: 2px solid #4CAF50;
  padding-bottom: 0.5rem;
}



.current-week-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  justify-content: center;
}

.week-label {
  color: #666;
  font-size: 0.9rem;
}

.week-date {
  color: #333;
  font-weight: 600;
  font-size: 1rem;
}

.week-number {
  color: #4CAF50;
  font-size: 0.9rem;
  font-weight: 600;
}



.teacher-availability-display.compact h4 {
  font-size: 1rem;
  margin-bottom: 1rem;
}

.teacher-availability-display.compact {
  padding: 1rem;
}

.teacher-availability-display.compact .weekday-section,
.teacher-availability-display.compact .weekend-section {
  margin-bottom: 1rem;
}

.teacher-availability-display.compact .time-slot {
  width: 100%;
  height: 35px;
}

.teacher-availability-display.compact .day-column {
  min-width: 100px;
  padding: 0.5rem;
}

.teacher-availability-display.compact .day-header {
  font-size: 0.8rem;
  padding: 0.3rem 0.6rem;
  min-width: 70px;
  margin-bottom: 0.5rem;
}

.teacher-availability-display.compact .time-text {
  font-size: 0.7rem;
}

.teacher-availability-display.compact .available-text,
.teacher-availability-display.compact .not-available-text,
.teacher-availability-display.compact .break-text {
  font-size: 0.6rem;
}



.teacher-availability-display h5 {
  margin: 1rem 0 0.75rem 0;
  color: #555;
  font-size: 1rem;
  font-weight: 600;
}

.weekday-section, .weekend-section {
  margin-bottom: 2rem;
}

/* Modern Yanal Kaydırma Stilleri */
.weekday-scroll-container,
.weekend-scroll-container {
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  background: linear-gradient(90deg, #f8f9fa 0%, #ffffff 20%, #ffffff 80%, #f8f9fa 100%);
  position: relative;
}

.weekday-scroll-container::before,
.weekend-scroll-container::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 20px;
  background: linear-gradient(90deg, #f8f9fa 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
}


.weekday-content,
.weekend-content {
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  min-width: max-content;
}

.day-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 140px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.day-column:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.day-header {
  font-weight: 700;
  color: #333;
  font-size: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border-radius: 20px;
  text-align: center;
  min-width: 100px;
}

.time-slots-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.time-slot {
  width: 100%;
  height: 45px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: white;
  transition: all 0.2s ease;
  cursor: default;
}

/* Time slot responsive tasarım */
@media (max-width: 768px) {
  .time-slot {
    height: 40px;
    border-width: 1px;
    border-radius: 6px;
  }
  
  .time-text {
    font-size: 0.75rem;
  }
  
  .break-text,
  .slot-status {
    font-size: 0.65rem;
  }
}

@media (max-width: 480px) {
  .time-slot {
    height: 35px;
    border-radius: 4px;
  }
  
  .time-text {
    font-size: 0.7rem;
  }
  
  .break-text,
  .slot-status {
    font-size: 0.6rem;
  }
}

.time-slot.available {
  border-color: #4CAF50;
  background-color: #e8f5e8;
}

.time-slot.disabled {
  background-color: #f5f5f5;
  border-color: #ddd;
  color: #999;
  opacity: 0.6;
}

.time-slot.booked {
  border-color: #ff9800;
  background-color: #fff3cd;
}

.time-slot.booked-by-student {
  border-color: #9c27b0;
  background-color: #e1bee7;
  cursor: not-allowed;
}

.time-text {
  font-size: 0.8rem;
  font-weight: 500;
  text-align: center;
}

.break-text {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  color: #856404;
}

.slot-status {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
}

.slot-status.available {
  color: #2e7d32;
}

.slot-status.not-available {
  color: #d32f2f;
}

.slot-status.booked {
  color: #ff9800;
}



.loading {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.error {
  text-align: center;
  padding: 1rem;
  color: #f44336;
  background-color: #ffebee;
  border-radius: 6px;
}

.no-availability {
  text-align: center;
  padding: 2rem;
  color: #999;
  font-style: italic;
}

/* Modern Scrollbar Stilleri */
.weekday-scroll-container::-webkit-scrollbar,
.weekend-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.weekday-scroll-container::-webkit-scrollbar-track,
.weekend-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
  margin: 0 20px;
}

.weekday-scroll-container::-webkit-scrollbar-thumb,
.weekend-scroll-container::-webkit-scrollbar-thumb {
  background: linear-gradient(90deg, #4CAF50, #45a049);
  border-radius: 4px;
  transition: background 0.2s ease;
}

.weekday-scroll-container::-webkit-scrollbar-thumb:hover,
.weekend-scroll-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(90deg, #45a049, #388e3c);
}

@media (max-width: 768px) {
  .teacher-availability-display {
    padding: 1rem;
  }

  .week-navigation {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }

  .week-nav-btn {
    min-width: 100px;
    font-size: 0.9rem;
  }

  .current-week-info {
    flex-direction: column;
    gap: 0.25rem;
  }

  .weekday-content,
  .weekend-content {
    gap: 1rem;
    padding: 1rem;
  }

  .day-column {
    min-width: 120px;
    padding: 0.75rem;
  }

  .day-header {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
    min-width: 80px;
  }

  .time-slot {
    height: 40px;
  }

  .time-text {
    font-size: 0.75rem;
  }

  .summary-stats {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Modal içinde kullanıldığında daha geniş görünüm */
.teacher-availability-display.modal-view {
  padding: 0;
}

.teacher-availability-display.modal-view .time-slots {
  gap: 0.75rem;
}

.teacher-availability-display.modal-view .time-slot {
  min-width: 110px;
  height: 45px;
}

.teacher-availability-display.modal-view .time-text {
  font-size: 0.85rem;
}

.teacher-availability-display.modal-view .available-text,
.teacher-availability-display.modal-view .not-available-text,
.teacher-availability-display.modal-view .break-text {
  font-size: 0.7rem;
}
</style>
